import React from 'react';
import PropTypes from 'prop-types';

import BadgeCollection from '../BadgeCollection';
import Icon from '../Icon';
import Link from '../Link';
import Rating from '../Rating';
import priceRangeToString from '../../utils/priceRangeToString';
import TranslationProvider from '../../providers/TranslationProvider';
import Chevron from '../../svg/icons/ic_chevron-left_20x20.svg';
import pinSVG from '../../svg/icons/ic_location_20x20.svg';

import './AffiliatedRestaurants.scss';

export const renderResyRating = (venueRater) => {
  if (!venueRater.rater || venueRater.rater?.length === 0 || venueRater.rater[0].name !== 'Resy') {
    return null;
  }

  const rating = venueRater.rater[0];

  return (
    <div
      data-testid="Affiliated-Restaurants-Rating-container"
      className="AffiliatedRestaurants__Rating__container"
      key={`${rating.name}_${rating.score}`}
    >
      {rating.score && (
        <>
          <Rating
            score={rating.score}
            totalRatings={rating.total}
          />
          <div className="separator" />
        </>
      )}
    </div>
  );
};

const AffiliatedRestaurants = ({
  isWidget,
  venues,
}) => {
  if (!venues || !venues.length) return null;

  const handleMouseEnter = (venueId) => {
    const chevron = document.getElementById(`Chevron-${venueId}`);
    if (chevron) {
      chevron.classList.add('AffiliatedRestaurants__chevron--active');
    }
  };

  const handleMouseLeave = (venueId) => {
    const chevron = document.getElementById(`Chevron-${venueId}`);
    if (chevron) {
      chevron.classList.remove('AffiliatedRestaurants__chevron--active');
    }
  };

  const { search } = window.location;

  return (
    <div className="AffiliatedRestaurants" data-testid="Affiliated-Restaurants">
      <TranslationProvider translationKey="you_might_also_like">
        <div className="AffiliatedRestaurants__title">
          You Might Also Like&hellip;
        </div>
      </TranslationProvider>

      <hr />

      {venues.map((venue, index) => (
        <div data-testid="Affiliated-Restaurants-venue-container" key={venue.name}>
          <div
            className="AffiliatedRestaurants__venue-container"
            onMouseEnter={() => handleMouseEnter(venue.id)}
            onMouseLeave={() => handleMouseLeave(venue.id)}
          >
            <div className="AffiliatedRestaurants__meta-container">
              <Link
                className="AffiliatedRestaurants__venue-name"
                href={isWidget ? `#/cities/${venue.location.url_slug}/venues/${venue.urlSlug}` : `cities/${venue.location.url_slug}/venues/${venue.urlSlug}${search}`}
              >
                {venue.name}
              </Link>
              <div className="AffiliatedRestaurants__cuisine-price">
                {renderResyRating({ rater: [{ name: 'Resy', score: venue.rating, total: venue.totalRatings }] })}
                {venue.cuisine
                  && (
                    <>
                      <span className="AffiliatedRestaurants__cuisine">{venue.cuisine}</span>
                      <div className="separator" />
                    </>
                  )}
                <span className="AffiliatedRestaurants__price-range">{priceRangeToString(venue.priceRange)}</span>
              </div>
              <div className="AffiliatedRestaurants__neighborhood">
                <Icon component={pinSVG} iconClass="pin" aria-hidden="true" />
                {venue.location.neighborhood}
              </div>

              <BadgeCollection
                badges={venue.collections}
                showLabels={false}
              />
            </div>

            <Icon
              name="Chevron"
              component={Chevron}
              className="AffiliatedRestaurants__chevron"
              id={`Chevron-${venue.id}`}
            />
          </div>
          {index !== venues.length - 1 && (
            <hr />
          )}
        </div>
      ))}
    </div>
  );
};

AffiliatedRestaurants.propTypes = {
  isWidget: PropTypes.bool,
  venues: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    location: PropTypes.shape({
      code: PropTypes.string.isRequired,
      neighborhood: PropTypes.string.isRequired,
    }).isRequired,
    cuisine: PropTypes.string.isRequired,
    collections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      file_name: PropTypes.string,
      image: PropTypes.string,
      short_name: PropTypes.string,
      type_id: PropTypes.number,
    })),
    priceRange: PropTypes.oneOfType([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired,
    ]),
    rating: PropTypes.number.isRequired,
    totalRatings: PropTypes.number.isRequired,
    urlSlug: PropTypes.string.isRequired,
  })).isRequired,
};

AffiliatedRestaurants.defaultProps = {
  isWidget: false,
};

AffiliatedRestaurants.displayName = 'AffiliatedRestaurants';
const affiliatedRestaurantsPropTypes = AffiliatedRestaurants.propTypes;
export { affiliatedRestaurantsPropTypes };
export default AffiliatedRestaurants;
